import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate } from "@angular/router";
import { Store } from "@ngrx/store";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { combineLatest, Observable, of } from "rxjs";
import { map } from "rxjs/operators";

import { AllowedAction, DisallowedActionCodes, KycState } from "../customer-management/customer-management.model";
import { CustomerManagementSelector } from "../customer-management/customer-management.selector";
import { CORE_ROUTE } from "../route";
import { CoreState } from "../state";

import { CustomerComplianceConfig } from "./customer-compliance.config";
import { CustomerComplianceService } from "./customer-compliance.service";
import { KycMessageService } from "./kyc-message.service";

@Injectable({
	providedIn: "root",
})
export class KycActionsGuardLogic {
	constructor(
		private customerComplianceService: CustomerComplianceService,
		private customerComplianceConfig: CustomerComplianceConfig,
		private store: Store<CoreState>,
		private kycMessageService: KycMessageService
	) {}

	// WARNING: used in micro-frontends, extra testing needed, speak to fe-evolution team for help
	canActivate(actionType?: keyof AllowedAction): Observable<string | boolean> {
		if (!actionType) {
			return of(true);
		}

		return combineLatest([
			this.customerComplianceService.kycStatusCheck$(actionType),
			this.store.select(RouteSelector.getCurrent),
			this.store.select(CustomerManagementSelector.getAllowedAction),
		]).pipe(
			map(([kyc, currentRoute, allowedActions]) => {
				if (this.customerComplianceConfig.getIsCustomerAssessmentsEnabled()) {
					const action = allowedActions && allowedActions[kyc.action];
					const assessmentRequired = action?.disallowedReasons?.includes(
						DisallowedActionCodes.M_CURRENTCUSTOMER_ASSESSMENTREQUIRED
					);

					if (assessmentRequired && !action?.isAllowed) {
						// do not show kyc message because a mandatory assessment is required
						return false;
					}
				}

				const { isAllowed } = kyc;

				if (!isAllowed) {
					const isDutchAndNotAccepted =
						kyc.complianceType === "Dutch" && kyc.currentStatus !== KycState.Accepted;
					const isAccessingBlockedResource =
						!!currentRoute?.queryParams?.returnUrl && currentRoute?.name === CORE_ROUTE.auth.login;
					if (!currentRoute || (isAccessingBlockedResource && !isDutchAndNotAccepted)) {
						return CORE_ROUTE.home;
					}

					if (isAccessingBlockedResource && isDutchAndNotAccepted) {
						return false;
					}

					this.kycMessageService.showMessage(kyc);
				}
				return isAllowed;
			})
		);
	}
}

@Injectable({
	providedIn: "root",
})
export class KycActionsGuard implements CanActivate {
	constructor(
		private kycActionsGuardLogic: KycActionsGuardLogic,
		private store: Store<CoreState>
	) {}

	canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
		return this.kycActionsGuardLogic.canActivate(route.data.actionType).pipe(
			map((guardResult) => {
				if (guardResult === false || guardResult === true) {
					return guardResult;
				}

				this.store.dispatch(RouteAction.navigate(guardResult));
				return false;
			})
		);
	}
}
